import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { authUtils } from "@/utils/auth";

/**
 * Props for the ProtectedRoute component
 * @interface ProtectedRouteProps
 * @property {React.ReactNode} children - The child components to be rendered if the user is authenticated
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * A higher-order component that protects routes from unauthorized access.
 * Redirects to the login page if the user is not authenticated.
 * 
 * @component
 * @param {ProtectedRouteProps} props - The component props
 * @returns {JSX.Element} The protected route component or a redirect to login
 * 
 * @example
 * <Route
 *   path="/dashboard"
 *   element={
 *     <ProtectedRoute>
 *       <Dashboard />
 *     </ProtectedRoute>
 *   }
 * />
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const location = useLocation();
  const isAuthenticated = authUtils.isAuthenticated();

  console.log(
    "ProtectedRoute: Checking authentication - Token exists:",
    isAuthenticated
  );

  // Check authentication status and redirect to login if not authenticated
  // The original location is preserved in the state for post-login redirection
  if (!isAuthenticated) {
    console.log("ProtectedRoute: Not authenticated, redirecting to /login");
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  console.log("ProtectedRoute: Authenticated, rendering children");
  return <>{children}</>;
};

export default ProtectedRoute;
